import React from "react"
import { useDispatch } from "react-redux"
import Head from "next/head"
import { VerticalTimeline } from "react-vertical-timeline-component"

import { Container, Card, Col, Row, Button } from "react-bootstrap"

import {
    toggleMakeEnquiryModal,
    setMakeEnquiryReqType,
} from "@/slices/makeEnquiryModalSlice"
import { toggleFindHomeModal } from "@/slices/findHomeModalSlice"

import MiscHeroImage from "@/main/miscHeroImage"
import CharacteristicsCarousel from "@/homeOwners/characteristicsCarousel"
import CharacteristicsStrip from "@/homeOwners/characteristicsStrip"
import TimelineElement from "@/homeOwners/timelineElement"
import InterestedCard from "@/homeOwners/interestedCard"
import ReviewCard from "@/homeOwners/reviewCard"

function BuyersScreen() {
    const dispatch = useDispatch()

    const url = "https://webbjenkins.co.uk/buyers/"
    const title = "Buying a Property on the Isle of Wight - Webb Jenkins"
    const description =
        "Get yourself on the property ladder. Whether you are looking for a forever home, or a buy-to-let, we will help you to find the perfect property for you."
    const imgUrl =
        "https://webb-jenkins-static-164679926.imgix.net/socials/webbjenkins-buying-social.jpg"
    const imgWidth = 1200
    const imgHeight = 630
    const imgAlt = "Webb Jenkins logo with buying your home caption"

    const description1 =
        "A local independent estate agency since 1998, our knowledge of the local area is unrivalled"
    const description2 =
        "Guiding you through the entire buying process, offering expert advice along the way"
    const description3 =
        "Active members of relevant bodies such as The Property Ombudsman Scheme, giving you peace of mind"
    const description4 =
        "Updating you at every step of the process so that you feel completely included and informed"

    return (
        <>
            <Head>
                <link rel="canonical" href={url} />
                <title>{title}</title>
                <meta name="description" content={description} />

                <meta property="fb:app_id" content="362437448786240" />
                <meta property="og:title" content={title} />
                <meta property="og:image:url" content={imgUrl} />
                <meta property="og:image:width" content={imgWidth} />
                <meta property="og:image:height" content={imgHeight} />
                <meta property="og:image:alt" content={imgAlt} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image:src" content={imgUrl} />
                <meta name="twitter:site" content="@WebbJenkinsIOW" />
            </Head>
            <MiscHeroImage
                title="Buying your home"
                subtitle="Buying the right home for you is one of the most important decisions you will make. We help you to find the perfect property, making the process less daunting"
                image="couple-buy-property.jpg"
                alt="Couple of landlords relaxing and smiling while checking their property online"
                position="center"
                buttonText="Find me a home"
                buttonOnClick={() => dispatch(toggleFindHomeModal(true))}
            />

            <Container fluid="md" as={Card} className="card-overlap-2 p-0">
                <Card.Body className="px-0">
                    <Card.Title>
                        <h2 className="text-center">
                            What you can expect from us
                        </h2>
                    </Card.Title>
                </Card.Body>
            </Container>

            <Container fluid="md" as={Card} className="d-sm-none mt-4 p-0">
                <Card.Body className="px-0">
                    <CharacteristicsCarousel
                        description1={description1}
                        description2={description2}
                        description3={description3}
                        description4={description4}
                    />
                </Card.Body>
            </Container>

            <CharacteristicsStrip
                description1={description1}
                description2={description2}
                description3={description3}
                description4={description4}
            />

            <Container fluid="md" className=" mt-4">
                <Row>
                    <Col className="text-center">
                        <Button
                            size="lg"
                            variant="warning"
                            onClick={() => {
                                dispatch(toggleMakeEnquiryModal(true))
                                dispatch(setMakeEnquiryReqType("general"))
                            }}
                            className="px-4 rounded-0"
                        >
                            <span className="text-bold text-white">
                                Make enquiry
                            </span>
                        </Button>
                    </Col>
                </Row>
            </Container>

            <Container fluid="md" as={Card} className="p-0 mt-5">
                <Card.Body>
                    <Card.Title>
                        <h2 className="text-center">The buying process</h2>
                    </Card.Title>
                </Card.Body>
            </Container>

            <Container fluid="md" className="mt-4">
                <VerticalTimeline
                    animate={false}
                    layout={"1-column-left"}
                    className="m-0 w-100 mw-100"
                >
                    <TimelineElement
                        position="left"
                        sequence={1}
                        image="buyers/registering-interest-in-property-purchase.jpg"
                        alt="Couple speaking to an agent about their interest in buying a property"
                        title="Register your interest with us"
                        description="Let us know your property requirements and we will help you with your search for your ideal home. We have an extensive mailing database where you will receive regular updates of new properties coming to the market."
                    />

                    <TimelineElement
                        position="right"
                        sequence={2}
                        image="buyers/approved-mortgage.jpg"
                        alt="An approved mortgage application"
                        title="Confirm your mortgage"
                        description="If you require a mortgage and do not already have one in place, we will put you in touch with an honest and reliable mortgage broker to obtain an agreement in principle. We will help arrange the mortgage valuation and if all is good you will then receive a mortgage offer."
                    />

                    <TimelineElement
                        position="left"
                        sequence={3}
                        image="buyers/attending-viewings.jpg"
                        alt="A couple admiring a house"
                        title="Attend viewings"
                        description="We will arrange viewings on your behalf at a time convenient to you. We will be present on the viewing, enabling us to offer advice and answer any questions that you may have. No pressure is applied as we strongly believe that only you can decide which is the perfect property for you."
                    />

                    <TimelineElement
                        position="right"
                        sequence={4}
                        image="buyers/make-offer-on-property.jpg"
                        alt="Woman excited that shes selected a home and made her first offer"
                        title="Make an offer"
                        description="Once you find the property for you, we will guide and help you to make your offer. We will negotiate on your behalf with the seller until a figure is agreed to proceed with your purchase."
                    />

                    <TimelineElement
                        position="left"
                        sequence={5}
                        image="buyers/offer-accepted-on-property.jpg"
                        alt="Keys to a new home being passed to the buyer of the property"
                        title="Exchange & complete"
                        description="The final part of the process is to exchange contracts, this is binding on both sides with a date mutually set to complete by both buyer and seller. Completion is the date that you  take ownership of your new home."
                    />
                </VerticalTimeline>
            </Container>
            <Container fluid="md">
                <Row className="justify-content-between d-flex">
                    <InterestedCard
                        title="Ready to find your new home?"
                        buttonTitle="Search properties"
                        transTypeId={1}
                    />
                    <ReviewCard
                        review="Buying through Steve and Mark at Webb Jenkins was a smooth and pleasant process. They gave me regular updates of new properties and rang to say that they have a property that is new to their books that appears to be just what we were looking for. How right they were, it was my dream home and went on to purchase in what I can only say was the smoothest property purchase that I have ever been involved in, well done team Webb Jenkins."
                        reviewer="Barbara"
                    />
                </Row>
            </Container>
        </>
    )
}

export default BuyersScreen
